/* ============================================
   ADDPROPERTY.JSX

   ============================================ */

import React, { useState, useContext, useRef, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Upload, Image as ImageIcon, Plus, X,
    Home, Building2, Trees, MapPin, Warehouse, Store, Search
} from 'lucide-react';
import { GoogleMap, Marker, Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import SellerMasterLayout from 'layouts/SellerMasterLayout';
import AppContext from 'context/AppContext';
import axios from 'axios';
import Swal from 'sweetalert2';
import '../../../assets/css/sellers/dashboard/sellerproperty/addproperty.css';

/* ── Google Maps config ───────────────────────────────────── */
const GOOGLE_MAPS_LIBRARIES = ['places'];
const DEFAULT_MAP_CENTER = { lat: 31.5204, lng: 74.3587 }; // Lahore default

/* ── Property type config ─────────────────────────────────── */
const PROPERTY_TYPES = [
    { value: 'House', label: 'House', icon: '🏠', color: '#22c55e', bg: '#f0fdf4' },
    { value: 'Apartment', label: 'Apartment', icon: '🏢', color: '#f97316', bg: '#fff7ed' },
    { value: 'Villa', label: 'Villa', icon: '🏡', color: '#8b5cf6', bg: '#f5f3ff' },
    { value: 'Plot', label: 'Plot', icon: '📐', color: '#22c55e', bg: '#f0fdf4' },
    { value: 'Commercial', label: 'Commercial', icon: '🏗️', color: '#3b82f6', bg: '#eff6ff' },
    { value: 'Farm House', label: 'Farm House', icon: '🌾', color: '#a16207', bg: '#fefce8' },
];

const CITIES = ['Lahore', 'Islamabad', 'Gujranwala'];
const STAGES = ['Ready to Move', 'Under Construction', 'Off Plan'];
const FURNISHED = ['Furnished', 'Semi Furnished', 'Unfurnished'];
const WATER_SRC = ['Municipal', 'Tube Well', 'Water Tank', 'Boring'];
const COMMERCIAL_TYPES = ['Office', 'Shop', 'Plaza', 'Warehouse', 'Showroom', 'Factory'];

/* ── Section wrapper ──────────────────────────────────────── */
const Section = ({ title, children }) => (
    <div className="sap-section">
        <p className="sap-section-title">{title}</p>
        <div className="sap-section-body">{children}</div>
    </div>
);

/* ── Field ────────────────────────────────────────────────── */
const Field = ({ label, required, children, half, third, quarter }) => {
    const cls = half ? 'sap-field sap-field--half'
        : third ? 'sap-field sap-field--third'
            : quarter ? 'sap-field sap-field--quarter'
                : 'sap-field';
    return (
        <div className={cls}>
            <label className="sap-label">{label}{required && <span className="sap-req"> *</span>}</label>
            {children}
        </div>
    );
};

/* ── Checkbox ─────────────────────────────────────────────── */
const Check = ({ label, checked, onChange }) => (
    <label className="sap-check">
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
        <span className="sap-check-box">{checked && '✓'}</span>
        <span className="sap-check-label">{label}</span>
    </label>
);

/* ── Main Component ───────────────────────────────────────── */
const AddProperty = () => {

    const locations = useLocation();
    const editingProperty = locations.state?.property || null;
    const isEditMode = locations.state?.mode === 'edit';

    const { Navigate, baseUrl, loggedInSeller, baseUrlForFiles, authToken } = useContext(AppContext);

    // ── Image state ──────────────────────────────────────────
    const [uploadedImages, setUploadedImages] = useState([]);
    const [imageFiles, setImageFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);

    //───────── BASIC INFO ─────────
    const [property_title, setproperty_title] = useState('');

    const [listing_Type, setListing_Type] = useState('FOR SALE');
    const [commercialType, setCommercialType] = useState('');
    //───────── LOCATION ─────────
    const [city, setCity] = useState('');
    const [location, setLocation] = useState('');
    const [full_Address, setFull_Address] = useState('');
    const [latitude, setLatitude] = useState('');
    const [longitude, setLongitude] = useState('');

    //───────── MAP ─────────
    const [mapCenter, setMapCenter] = useState(DEFAULT_MAP_CENTER);
    const [markerPos, setMarkerPos] = useState(null);
    const [searchLocationText, setSearchLocationText] = useState('');
    const autocompleteRef = useRef(null);
    const mapRef = useRef(null);

    const { isLoaded: isMapLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        // libraries: GOOGLE_MAPS_LIBRARIES,
    });

    const onMapLoad = useCallback((map) => { mapRef.current = map; }, []);

    const applyLatLng = (lat, lng) => {
        setMarkerPos({ lat, lng });
        setMapCenter({ lat, lng });
        setLatitude(lat.toFixed(6));
        setLongitude(lng.toFixed(6));
        reverseGeocoding(lat, lng);
    };
    const reverseGeocoding = (lat, lng) => {
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        axios.get(
            "https://maps.googleapis.com/maps/api/geocode/json",
            {
                params: {
                    latlng: `${lat},${lng}`,
                    key: apiKey,
                },
            }
        )
            .then((response) => {
                if (
                    response.data.status === "OK" &&
                    response.data.results &&
                    response.data.results.length > 0
                ) {
                    const formattedAddress =
                        response.data.results[0].formatted_address;

                    setFull_Address(formattedAddress);
                    setSearchLocationText(formattedAddress);
                    console.log("Formatted Address:", formattedAddress);
                } else {
                    console.log(
                        "Google Geocoding failed:",
                        response.data.status
                    );
                }
            })
            .catch((error) => {
                console.log("Reverse Geocoding Error:", error);
            });
    };
    const onAutocompleteLoad = (autocomplete) => { autocompleteRef.current = autocomplete; };
    const onMapClick = (e) => {
        applyLatLng(e.latLng.lat(), e.latLng.lng());
    };

    const onMarkerDragEnd = (e) => {
        applyLatLng(e.latLng.lat(), e.latLng.lng());
    };

    //───────── PROPERTY INFO ─────────
    const [size, setSize] = useState('');
    const [landArea, setLandArea] = useState('');
    const [price, setPrice] = useState('');
    //───────── ROOMS ─────────
    const [bedrooms, setBedrooms] = useState('');
    const [bathrooms, setBathrooms] = useState('');
    const [washrooms, setWashrooms] = useState('');
    const [parking_Spaces, setParking_Spaces] = useState('');
    const [floors, setFloors] = useState('');
    const [floorNumber, setFloorNumber] = useState('');
    //───────── PLOT INFO ─────────
    const [plotNumber, setPlotNumber] = useState('');
    //───────── FEATURES (BOOLEAN) ─────────
    const [cornerPlot, setCornerPlot] = useState(false);
    const [boundaryWall, setBoundaryWall] = useState(false);
    const [gardenAvailable, setGardenAvailable] = useState(false);
    const [balcony, setBalcony] = useState(false);
    const [servantQuarters, setServantQuarters] = useState(false);
    const [swimmingPool, setSwimmingPool] = useState(false);
    const [liftAvailable, setLiftAvailable] = useState(false);
    const [electricity, setElectricity] = useState(false);
    const [gas, setGas] = useState(false);
    const [internet, setInternet] = useState(false);
    //───────── UTILITIES ─────────
    const [waterSource, setWaterSource] = useState('');
    //───────── STATUS / META ─────────
    const [propertyStage, setPropertyStage] = useState('');
    const [furnished, setFurnished] = useState('');
    const [yearBuilt, setYearBuilt] = useState('');
    const [status, setStatus] = useState('Active');
    //───────── DESCRIPTION ─────────
    const [description, setDescription] = useState('');
    const [features, setFeatures] = useState('');
    //───────── OPTIONAL / UNUSED ─────────
    const [actions, setActions] = useState('');

    // ── property state ───────────────────────────────────────────
    const [propertyType, setPropertyType] = useState('');


    const [loading, setLoading] = useState(false);



    // ── Image handlers ───────────────────────────────────────
    const handleImageFiles = (files) => {
        const valid = Array.from(files).filter(f => f.type.startsWith('image/'));
        if (uploadedImages.length + valid.length > 3) {
            Swal.fire('Max 3 images allowed', '', 'warning');
            return;
        }
        valid.forEach(file => {
            const reader = new FileReader();
            reader.onload = e => setUploadedImages(prev => [...prev, e.target.result]);
            reader.readAsDataURL(file);
        });
        setImageFiles(prev => [...prev, ...valid].slice(0, 3));
    };

    const removeImage = i => {
        setUploadedImages(prev => prev.filter((_, idx) => idx !== i));
        setImageFiles(prev => prev.filter((_, idx) => idx !== i));
    };



    // ── Current type config ──────────────────────────────────
    const typeConf = PROPERTY_TYPES.find(t => t.value === propertyType);
    /* ── api call of add property ─────────────────────────────────────────────── */
    const handleSubmit = () => {
        const formData = new FormData();

        if (isEditMode) {
            formData.append('id', editingProperty.id);
        }

        formData.append('exseller_id', loggedInSeller?.id);
        formData.append('property_title', property_title);
        formData.append('property_type', propertyType);
        formData.append('listingType', listing_Type);
        formData.append('commercial_type', commercialType || '');
        formData.append('city', city);
        formData.append('location', location);
        formData.append('full_address', full_Address);
        formData.append('latitude', latitude || '');
        formData.append('longitude', longitude || '');
        formData.append('size', size);
        formData.append('land_area', landArea);
        formData.append('price', price);
        formData.append('bedrooms', bedrooms || 0);
        formData.append('bathrooms', bathrooms || 0);
        formData.append('washrooms', washrooms || 0);
        formData.append('parking_spaces', parking_Spaces || 0);
        formData.append('floors', floors || 0);
        formData.append('floor_number', floorNumber || 0);
        formData.append('plot_number', plotNumber || '');
        formData.append('corner_plot', cornerPlot ? 1 : 0);
        formData.append('boundary_wall', boundaryWall ? 1 : 0);
        formData.append('garden_available', gardenAvailable ? 1 : 0);
        formData.append('balcony', balcony ? 1 : 0);
        formData.append('servant_quarters', servantQuarters ? 1 : 0);
        formData.append('swimming_pool', swimmingPool ? 1 : 0);
        formData.append('lift_available', liftAvailable ? 1 : 0);
        formData.append('electricity', electricity ? 1 : 0);
        formData.append('gas', gas ? 1 : 0);
        formData.append('internet', internet ? 1 : 0);
        formData.append('water_source', waterSource || '');
        formData.append('property_stage', propertyStage || '');
        formData.append('furnished', furnished || '');
        formData.append('year_built', yearBuilt || '');
        formData.append('status', status || 'Active');
        formData.append('description', description || '');
        formData.append('features', features || '');

        if (imageFiles.length > 0) {
            imageFiles.forEach((image) => {
                formData.append("images[]", image);
            });
        }

        setLoading(true);

        const endpoint = isEditMode
            ? `${baseUrl}/seller/sellerproperties/updateProperty`
            : `${baseUrl}/seller/sellerproperties/addProperty`;

        axios.post(endpoint, formData, {
            headers: {
                "Content-Type": "multipart/formdata",
                "Authorization": `Bearer ${authToken}`
            },
        })
            .then((response) => {
                setLoading(false);
                if (response.data.success === true) {
                    Swal.fire({
                        title: 'Congratulations!',
                        text: isEditMode ? 'Property Updated Successfully.' : 'Property Added Successfully.',
                        icon: 'success',
                        confirmButtonText: 'Awesome!'
                    }).then(() => Navigate('/seller/my-property'));
                } else {
                    Swal.fire('Error', 'Something went wrong.', 'error');
                }
            })
            .catch((error) => {
                setLoading(false);
                console.log(error.response);
                Swal.fire('Error', 'Something went wrong.', 'error');
            });
    };

    useEffect(() => {
        if (isEditMode && editingProperty) {
            const p = editingProperty;

            setproperty_title(p.property_title || '');
            setPropertyType(p.property_type || '');
            setListing_Type(p.listingType || 'FOR SALE');
            setCommercialType(p.commercial_type || '');
            setCity(p.city || '');
            setLocation(p.location || '');
            setFull_Address(p.full_address || '');
            setLatitude(p.latitude || '');
            setLongitude(p.longitude || '');

            if (p.latitude && p.longitude) {
                const lat = parseFloat(p.latitude);
                const lng = parseFloat(p.longitude);
                setMarkerPos({ lat, lng });
                setMapCenter({ lat, lng });
            }

            setSize(p.size || '');
            setLandArea(p.land_area || '');
            setPrice(p.price || '');

            setBedrooms(p.bedrooms || '');
            setBathrooms(p.bathrooms || '');
            setWashrooms(p.washrooms || '');
            setParking_Spaces(p.parking_spaces || '');
            setFloors(p.floors || '');
            setFloorNumber(p.floor_number || '');

            setPlotNumber(p.plot_number || '');

            setCornerPlot(!!Number(p.corner_plot));
            setBoundaryWall(!!Number(p.boundary_wall));
            setGardenAvailable(!!Number(p.garden_available));
            setBalcony(!!Number(p.balcony));
            setServantQuarters(!!Number(p.servant_quarters));
            setSwimmingPool(!!Number(p.swimming_pool));
            setLiftAvailable(!!Number(p.lift_available));
            setElectricity(!!Number(p.electricity));
            setGas(!!Number(p.gas));
            setInternet(!!Number(p.internet));

            setWaterSource(p.water_source || '');
            setPropertyStage(p.property_stage || '');
            setFurnished(p.furnished || '');
            setYearBuilt(p.year_built || '');
            setStatus(p.status || 'Active');

            setDescription(p.description || '');
            setFeatures(p.features || '');

            // Existing images preview (agar backend se URL milte hain)
            if (p.expropertyimages?.length > 0) {
                setUploadedImages(p.expropertyimages.map(img => `${baseUrlForFiles}${img.images}`));
            }
        }
    }, [isEditMode, editingProperty]);

    /* ── RENDER ─────────────────────────────────────────────── */
    return (
        <SellerMasterLayout pageTitle="Add Property">
            <div className="sap-page">

                {/* ── Page Header ── */}
                <div className="sap-page-header">
                    <div>
                        <h1 className="sap-page-title">{isEditMode ? 'Edit Property' : 'Add New Property'}</h1>
                        <p className="sap-page-sub">
                            {isEditMode ? 'Update the details below' : 'Fill in the details below to list your property'}
                        </p>
                    </div>
                    <button className="sap-cancel-top-btn" onClick={() => Navigate('/seller/my-property')}>
                        <X size={16} /> Cancel
                    </button>
                </div>

                <div className="sap-layout">

                    {/* ════ LEFT — Main property ════ */}
                    <div className="sap-main">

                        {/* ── Property Type Selector ── */}
                        <div className="sap-card">
                            <p className="sap-card-title">Select Property Type <span className="sap-req">*</span></p>
                            <div className="sap-type-grid">
                                {PROPERTY_TYPES.map(t => (
                                    <button
                                        key={t.value}
                                        type="button"
                                        className={`sap-type-btn ${propertyType === t.value ? 'sap-type-btn--active' : ''}`}
                                        style={propertyType === t.value ? { borderColor: t.color, background: t.bg, color: t.color } : {}}
                                        onClick={() => setPropertyType(t.value)}
                                    >
                                        <span className="sap-type-icon">{t.icon}</span>
                                        <span className="sap-type-label">{t.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* ── Property Type Badge (when selected) ── */}
                        {typeConf && (
                            <div className="sap-type-badge-bar" style={{ background: typeConf.bg, borderColor: typeConf.color }}>
                                <span style={{ color: typeConf.color, fontWeight: 700, fontSize: 16 }}>
                                    {typeConf.icon} {typeConf.label}
                                </span>
                                <span style={{ color: typeConf.color, fontSize: 13 }}>
                                    Fill in the fields specific to this property type
                                </span>
                            </div>
                        )}

                        {/* ── Image Upload ── */}
                        <div className="sap-card">
                            <p className="sap-card-title">Property Images <span className="sap-req">*</span> <span className="sap-card-hint">(Upload 2-3 images)</span></p>
                            <div
                                className={`sap-upload-area ${isDragging ? 'sap-upload-area--drag' : ''} ${uploadedImages.length > 0 ? 'sap-upload-area--has' : ''}`}
                                onDrop={e => { e.preventDefault(); setIsDragging(false); handleImageFiles(e.dataTransfer.files); }}
                                onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                                onDragLeave={() => setIsDragging(false)}
                            >
                                {uploadedImages.length > 0 ? (
                                    <div className="sap-img-previews">
                                        {uploadedImages.map((img, i) => (
                                            <div key={i} className="sap-img-item">
                                                <img src={img} alt="" />
                                                <button type="button" className="sap-img-remove" onClick={() => removeImage(i)}><X size={14} /></button>
                                            </div>
                                        ))}
                                        {uploadedImages.length < 3 && (
                                            <label className="sap-img-add" htmlFor="sap-file-input">
                                                <Plus size={24} /><span>Add More</span>
                                            </label>
                                        )}
                                    </div>
                                ) : (
                                    <div className="sap-upload-placeholder">
                                        <ImageIcon size={40} color="#cbd5e1" />
                                        <p className="sap-upload-title">Drag & drop images here</p>
                                        <p className="sap-upload-or">or</p>
                                        <label className="sap-browse-btn" htmlFor="sap-file-input">
                                            <Upload size={15} /> Browse Files
                                        </label>
                                    </div>
                                )}
                                <input id="sap-file-input" type="file" accept="image/*" multiple onChange={e => handleImageFiles(e.target.files)} style={{ display: 'none' }} />
                            </div>
                            <p className="sap-upload-count">{uploadedImages.length} / 3 images uploaded</p>
                        </div>

                        {/* ── Basic Info ── */}
                        <div className="sap-card">
                            <p className="sap-card-title">Basic Inpropertyation</p>
                            <div className="sap-fields-wrap">

                                <Field label="Property Title" required>
                                    <input className="sap-input" placeholder="e.g., Modern 5 Marla House in DHA"
                                        value={property_title} onChange={e => setproperty_title(e.target.value)} />
                                </Field>

                                <div className="sap-row">
                                    <Field label="Listing Type" required half>
                                        <select className="sap-select" value={listing_Type} onChange={e => setListing_Type(e.target.value)}>
                                            <option value="FOR SALE">For Sale</option>
                                            <option value="FOR RENT">For Rent</option>
                                        </select>
                                    </Field>
                                    <Field label="Price" required half>
                                        <input className="sap-input" placeholder="e.g., PKR 2.5 Crore"
                                            value={price} onChange={e => setPrice(e.target.value)} />
                                    </Field>
                                </div>

                                {/* Commercial type — only for Commercial */}
                                {propertyType === 'Commercial' && (
                                    <Field label="Commercial Type" required>
                                        <select className="sap-select" value={commercialType} onChange={e => setCommercialType(e.target.value)}>
                                            <option value="">Select Commercial Type</option>
                                            {COMMERCIAL_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </Field>
                                )}

                            </div>
                        </div>

                        {/* ── Location ── */}
                        <div className="sap-card">
                            <p className="sap-card-title">Location</p>
                            <div className="sap-fields-wrap">
                                <div className="sap-row">
                                    <Field label="Location" required half>
                                        <input className="sap-input" placeholder="e.g., DHA Phase 5, Lahore"
                                            value={location} onChange={e => setLocation(e.target.value)} />
                                    </Field>
                                    <Field label="City" required half>
                                        <select className="sap-select" value={city} onChange={e => setCity(e.target.value)}>
                                            <option value="">Select City</option>
                                            {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </Field>
                                </div>
                                <Field label="Full Address" required>
                                    <input className="sap-input" placeholder="Street no, Block, Area, City"
                                        value={full_Address} onChange={e => setFull_Address(e.target.value)} />
                                </Field>

                                {/* ── Search Exact Location ── */}
                                {/* <Field label="Search Exact Location">
                                    <div className="sap-map-search-wrap">
                                        <Search size={15} className="sap-map-search-icon" />
                                        {isMapLoaded ? (
                                            <Autocomplete
                                                onLoad={onAutocompleteLoad}
                                                onPlaceChanged={onPlaceChanged}
                                            >
                                                <input
                                                    className="sap-input sap-map-search-input"
                                                    placeholder="Search location..."
                                                    value={searchLocationText}
                                                    onChange={e => setSearchLocationText(e.target.value)}
                                                />
                                            </Autocomplete>
                                        ) : (
                                            <input
                                                className="sap-input sap-map-search-input"
                                                placeholder="Search location..."
                                                disabled
                                            />
                                        )}
                                    </div>
                                </Field> */}

                                {/* ── Google Map ── */}
                                <div className="sap-map-container">
                                    {isMapLoaded ? (
                                        <GoogleMap
                                            mapContainerClassName="sap-map-canvas"
                                            center={mapCenter}
                                            zoom={markerPos ? 15 : 12}
                                            onLoad={onMapLoad}
                                            onClick={onMapClick}
                                            options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
                                        >
                                            {markerPos && (
                                                <Marker position={markerPos} draggable onDragEnd={onMarkerDragEnd} />
                                            )}
                                        </GoogleMap>
                                    ) : (
                                        <div className="sap-map-placeholder">
                                            <MapPin size={28} />
                                            <span>Loading map...</span>
                                        </div>
                                    )}
                                </div>

                                <div className="sap-row">
                                    <Field label="Latitude" half>
                                        <input className="sap-input" placeholder="e.g., 31.4697"
                                            value={latitude} readOnly />
                                    </Field>
                                    <Field label="Longitude" half>
                                        <input className="sap-input" placeholder="e.g., 74.2728"
                                            value={longitude} readOnly />
                                    </Field>
                                </div>
                            </div>
                        </div>

                        {/* ── Property Details (Dynamic) ── */}
                        {propertyType && (
                            <div className="sap-card">
                                <p className="sap-card-title">Property Details</p>
                                <div className="sap-fields-wrap">

                                    {/* ─ Size & Land Area (all types) ─ */}
                                    <div className="sap-row">
                                        <Field label="Size (sq ft)" required half>
                                            <input className="sap-input" type="number" placeholder="e.g., 2500"
                                                value={size} onChange={e => setSize(e.target.value)} />
                                        </Field>
                                        <Field label="Land Area (Marla/Kanal)" half>
                                            <input className="sap-input" placeholder="e.g., 10 Marla"
                                                value={landArea} onChange={e => setLandArea(e.target.value)} />
                                        </Field>
                                    </div>

                                    {/* ─ House / Villa / Farm House ─ */}
                                    {['House', 'Villa', 'Farm House'].includes(propertyType) && (
                                        <>
                                            <div className="sap-row">
                                                <Field label="Bedrooms" required third>
                                                    <input className="sap-input" type="number" placeholder="3"
                                                        value={bedrooms} onChange={e => setBedrooms(e.target.value)} />
                                                </Field>
                                                <Field label="Bathrooms" required third>
                                                    <input className="sap-input" type="number" placeholder="2"
                                                        value={bathrooms} onChange={e => setBathrooms(e.target.value)} />
                                                </Field>
                                                <Field label="Parking Spaces" third>
                                                    <input className="sap-input" type="number" placeholder="2"
                                                        value={parking_Spaces} onChange={e => setParking_Spaces(e.target.value)} />
                                                </Field>
                                            </div>
                                            <div className="sap-row">
                                                <Field label="Floors" third>
                                                    <input className="sap-input" type="number" placeholder="2"
                                                        value={floors} onChange={e => setFloors(e.target.value)} />
                                                </Field>
                                            </div>
                                        </>
                                    )}

                                    {/* ─ Apartment ─ */}
                                    {propertyType === 'Apartment' && (
                                        <>
                                            <div className="sap-row">
                                                <Field label="Bedrooms" required quarter>
                                                    <input className="sap-input" type="number" placeholder="2"
                                                        value={bedrooms} onChange={e => setBedrooms(e.target.value)} />
                                                </Field>
                                                <Field label="Bathrooms" required quarter>
                                                    <input className="sap-input" type="number" placeholder="1"
                                                        value={bathrooms} onChange={e => setBathrooms(e.target.value)} />
                                                </Field>
                                                <Field label="Washrooms" quarter>
                                                    <input className="sap-input" type="number" placeholder="1"
                                                        value={washrooms} onChange={e => setWashrooms(e.target.value)} />
                                                </Field>
                                                <Field label="Parking Spaces" quarter>
                                                    <input className="sap-input" type="number" placeholder="1"
                                                        value={parking_Spaces} onChange={e => setParking_Spaces(e.target.value)} />
                                                </Field>
                                            </div>
                                            <div className="sap-row">
                                                <Field label="Floor Number" required half>
                                                    <input className="sap-input" type="number" placeholder="e.g., 5"
                                                        value={floorNumber} onChange={e => setFloorNumber(e.target.value)} />
                                                </Field>
                                            </div>
                                        </>
                                    )}

                                    {/* ─ Plot ─ */}
                                    {propertyType === 'Plot' && (
                                        <div className="sap-row">
                                            <Field label="Plot Number" required third>
                                                <input className="sap-input" placeholder="e.g., 123"
                                                    value={plotNumber} onChange={e => setPlotNumber(e.target.value)} />
                                            </Field>
                                        </div>
                                    )}

                                    {/* ─ Commercial ─ */}
                                    {propertyType === 'Commercial' && (
                                        <div className="sap-row">
                                            <Field label="Washrooms" third>
                                                <input className="sap-input" type="number" placeholder="2"
                                                    value={washrooms} onChange={e => setWashrooms(e.target.value)} />
                                            </Field>
                                            <Field label="Parking Spaces" third>
                                                <input className="sap-input" type="number" placeholder="3"
                                                    value={parking_Spaces} onChange={e => setParking_Spaces(e.target.value)} />
                                            </Field>
                                            <Field label="Floors" third>
                                                <input className="sap-input" type="number" placeholder="1"
                                                    value={floors} onChange={e => setFloors(e.target.value)} />
                                            </Field>
                                        </div>
                                    )}

                                </div>
                            </div>
                        )}

                        {/* ── Amenities (not for Plot/Commercial) ── */}
                        {propertyType && !['Plot', 'Commercial'].includes(propertyType) && (
                            <div className="sap-card">
                                <p className="sap-card-title">Amenities</p>
                                <div className="sap-checks-grid">
                                    <Check
                                        label="Garden Available"
                                        checked={gardenAvailable}
                                        onChange={setGardenAvailable}
                                    />

                                    <Check
                                        label="Balcony"
                                        checked={balcony}
                                        onChange={setBalcony}
                                    />

                                    <Check
                                        label="Servant Quarters"
                                        checked={servantQuarters}
                                        onChange={setServantQuarters}
                                    />

                                    <Check
                                        label="Swimming Pool"
                                        checked={swimmingPool}
                                        onChange={setSwimmingPool}
                                    />

                                    <Check
                                        label="Boundary Wall"
                                        checked={boundaryWall}
                                        onChange={setBoundaryWall}
                                    />
                                    {/* Corner Plot only for House/Villa/Farm House */}
                                    {['House', 'Villa', 'Farm House'].includes(propertyType) && (
                                        <Check label="Corner Plot" checked={cornerPlot} onChange={setCornerPlot} />
                                    )}
                                    {/* Lift for Apartment */}
                                    {propertyType === 'Apartment' && (
                                        <>
                                            <Check label="Balcony" checked={balcony} onChange={setBalcony} />
                                            <Check label="Lift Available" checked={liftAvailable} onChange={setLiftAvailable} />
                                            <Check label="Servant Quarters" checked={servantQuarters} onChange={setServantQuarters} />
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ── Plot-specific amenities ── */}
                        {propertyType === 'Plot' && (
                            <div className="sap-card">
                                <p className="sap-card-title">Plot Details</p>
                                <div className="sap-checks-grid">
                                    <Check label="Corner Plot" checked={cornerPlot} onChange={setCornerPlot} />
                                    <Check label="Boundary Wall" checked={boundaryWall} onChange={setBoundaryWall} />
                                </div>
                            </div>
                        )}

                        {/* ── Utilities ── */}
                        {propertyType && (
                            <div className="sap-card">
                                <p className="sap-card-title">Utilities</p>
                                <div className="sap-fields-wrap">
                                    <div className="sap-row">
                                        <Field label="Water Source" half>
                                            <select className="sap-select" value={waterSource} onChange={e => setWaterSource(e.target.value)}>
                                                <option value="">Select Water Source</option>
                                                {WATER_SRC.map(w => <option key={w} value={w}>{w}</option>)}
                                            </select>
                                        </Field>
                                    </div>
                                    <div className="sap-checks-grid">
                                        <Check label="Electricity" checked={electricity} onChange={setElectricity} />
                                        <Check label="Gas" checked={gas} onChange={setGas} />
                                        <Check label="Internet" checked={internet} onChange={setInternet} />
                                        {(propertyType === 'Apartment' || propertyType === 'Commercial') && (
                                            <Check label="Lift Available" checked={liftAvailable} onChange={setLiftAvailable} />
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── Property Meta ── */}
                        {propertyType && (
                            <div className="sap-card">
                                <p className="sap-card-title">Additional Info</p>
                                <div className="sap-fields-wrap">
                                    <div className="sap-row">
                                        <Field label="Property Stage" third>
                                            <select
                                                className="sap-select"
                                                value={propertyStage}
                                                onChange={e => setPropertyStage(e.target.value)}
                                            >
                                                <option value="">Select Stage</option>
                                                {STAGES.map(s => (
                                                    <option key={s} value={s}>
                                                        {s}
                                                    </option>
                                                ))}
                                            </select>
                                        </Field>
                                        {!['Plot'].includes(propertyType) && (
                                            <Field label="Furnished" third>
                                                <select className="sap-select" value={furnished} onChange={e => setFurnished(e.target.value)}>
                                                    <option value="">Select Furnished</option>
                                                    {FURNISHED.map(f => <option key={f} value={f}>{f}</option>)}
                                                </select>
                                            </Field>
                                        )}
                                        <Field label="Year Built" third>
                                            <input className="sap-input" type="number" placeholder={`e.g., ${new Date().getFullYear()}`}
                                                min="1900" max={new Date().getFullYear()}
                                                value={yearBuilt} onChange={e => setYearBuilt(e.target.value)} />
                                        </Field>
                                    </div>

                                    <Field label="Description">
                                        <textarea className="sap-textarea" rows={4}
                                            placeholder="Enter detailed property description..."
                                            value={description} onChange={e => setDescription(e.target.value)} />
                                    </Field>

                                    <Field label="Features (comma separated)">
                                        <textarea className="sap-textarea" rows={2}
                                            placeholder="e.g., Swimming Pool, Gym, Security, CCTV"
                                            value={features} onChange={e => setFeatures(e.target.value)} />
                                    </Field>
                                </div>
                            </div>
                        )}

                    </div>
                    {/* /sap-main */}

                    {/* ════ RIGHT — Summary Sidebar ════ */}
                    <div className="sap-sidebar">
                        <div className="sap-summary-card">
                            <p className="sap-card-title">Listing Summary</p>

                            <div className="sap-summary-type">
                                {typeConf ? (
                                    <span className="sap-summary-badge" style={{ background: typeConf.bg, color: typeConf.color, borderColor: typeConf.color }}>
                                        {typeConf.icon} {typeConf.label}
                                    </span>
                                ) : (
                                    <span className="sap-summary-empty">No type selected</span>
                                )}
                            </div>

                            <div className="sap-summary-rows">
                                <div className="sap-summary-row">
                                    <span>Title</span>
                                    <span>{property_title || '—'}</span>
                                </div>
                                <div className="sap-summary-row">
                                    <span>Price</span>
                                    <span className="sap-summary-price">{price || '—'}</span>
                                </div>
                                <div className="sap-summary-row">
                                    <span>Listing</span>
                                    <span>{listing_Type === 'FOR SALE' ? 'For Sale' : 'For Rent'}</span>
                                </div>
                                <div className="sap-summary-row">
                                    <span>City</span>
                                    <span>{city || '—'}</span>
                                </div>
                                <div className="sap-summary-row">
                                    <span>Images</span>
                                    <span>{uploadedImages.length} / 3</span>
                                </div>
                            </div>

                            <div className="sap-summary-divider" />

                            <button
                                className="sap-submit-btn"
                                style={typeConf ? { background: typeConf.color } : {}}
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : (isEditMode ? `Update ${propertyType || 'Property'}` : `Add ${propertyType || 'Property'}`)}
                            </button>

                            <button className="sap-cancel-btn" onClick={() => Navigate('/seller/my-property')}>
                                Cancel
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        </SellerMasterLayout>
    );
};

export default AddProperty;